import { useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import IconButton from "../../../components/Buttons/IconButton";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startCreateOwnerUser } from "../../../actions/owner";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  BUTTON_CANCEL,
  BUTTON_CREATE,
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_REGISTERED,
  FIELD_EMAIL_VALID,
  FIELD_PASSWORD_INSECURE,
  FIELD_REQUIRED,
  MODAL_CREATE_OWNER_USER_ARIA_LABEL,
  MODAL_CREATE_OWNER_USER_DESCRIPTION,
  MODAL_CREATE_OWNER_USER_SUCCESS,
  MODAL_CREATE_OWNER_USER_TITLE,
  NAME_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../../utils/regex";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import { auth } from "../../../lib/firebase";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";

const ModalCreateOwnerUser = ({
  modalOpen = false,
  onCloseModal = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, formState, handleSubmit, reset, setError } = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const onChangeShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (formValues) => {
    const { name, email, password } = formValues;

    setIsSubmitting(true);

    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setError("email", {
          type: "manual",
          message: t(FIELD_EMAIL_REGISTERED),
        });
        return;
      }

      const response = await dispatch(
        startCreateOwnerUser({
          name,
          email,
          password,
        })
      );

      if (response?.ok) {
        await SimpleAlert({
          title: t(MODAL_CREATE_OWNER_USER_SUCCESS),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        reset();
        onCloseModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_CREATE_OWNER_USER_TITLE)}
      description={t(MODAL_CREATE_OWNER_USER_DESCRIPTION)}
      ariaLabel={t(MODAL_CREATE_OWNER_USER_ARIA_LABEL)}
      maxWidth="xs"
      fullWidth
      scroll="paper"
      sxTitle={{ color: "background.paperContrastText" }}
      sxDescription={{ color: "background.paperContrastText" }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <TextField
          fullWidth
          label={t(NAME_PLACEHOLDER)}
          name="name"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="email"
          label={t(EMAIL_PLACEHOLDER)}
          name="email"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
                return true;
              },
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
              return true;
            },
          }}
          errors={errors}
        />

        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            loading: isSubmitting,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_CREATE,
            loading: isSubmitting,
            onClick: () => {},
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreateOwnerUser;
